import { Icon, XOutlineIcon } from '@fountain/ripple/icon';
import { ApprovalsService } from 'api-clients/monolith';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useApiService } from 'hooks/useApiService';
import globalMessages from 'shared/global/messages';

import { useStyles as useDialogStyles } from '../styles';
import { SubjectType, UserOption } from '../types';
import messages from './messages';
import { useStyles } from './styles';
import { Approver, Level } from './types';

interface ReplaceApproverDialogProps {
  /** The level the departed approver is on. */
  level: Level;
  /** The approver who left the account. */
  approver: Approver;
  subjectType: SubjectType;
  submitting: boolean;
  /** What the server said when the change was refused, verbatim. */
  error?: string;
  onCancel: () => void;
  /** Null removes the approver with nobody in their place. */
  onConfirm: (replacementUserId: string | null) => void;
}

const TITLE_ID = 'replace-approver-title';
const FIELD_ID = 'replace-approver-field';

/**
 * Replaces or removes an approver who left the account, on one request (the
 * offer-approvals prototype's Replace approver dialog).
 *
 * The rule is not edited: a request runs its pinned snapshot, and the next
 * request resolves its approvers from the rule as it stands then. So the
 * prototype's "the rule will be updated" banner is not drawn, and its "Remove
 * from rule" reads "Remove from request".
 *
 * Remove is offered only when somebody else stays on the level to clear it.
 * The server refuses a removal of the level's last primary, which would leave a
 * level nothing can ever clear, so the control would fail every time it was
 * pressed.
 *
 * Replace stays enabled with nobody chosen and says so on the press, as
 * `layout-composition`'s progressive-action rule asks. It is disabled only
 * while there is no list to choose from, loading, failed or empty, because the
 * blocker it would print lives on the field, and the body already says why.
 *
 * The candidates are the users the rule editor's picker offers for this
 * subject type, less anybody already on the level: the server refuses a second
 * place on one level.
 */
export const ReplaceApproverDialog: React.FC<ReplaceApproverDialogProps> = ({
  level,
  approver,
  subjectType,
  submitting,
  error,
  onCancel,
  onConfirm,
}) => {
  const styles = useDialogStyles();
  const queueStyles = useStyles();
  const intl = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [choice, setChoice] = useState('');
  const [showRequired, setShowRequired] = useState(false);

  const { result, refetch } = useApiService<{ approvers: UserOption[] }>(
    useCallback(() => ApprovalsService.getInternalApiApprovalsApproverOptions(), []),
  );

  const onLevel = useMemo(
    () => new Set(level.approvers.map(other => other.id)),
    [level.approvers],
  );
  const candidates = useMemo(
    () =>
      result.status === 'ready'
        ? result.data.approvers.filter(
            option =>
              option.subject_types.includes(subjectType) &&
              !onLevel.has(option.external_id),
          )
        : [],
    [result, subjectType, onLevel],
  );

  // Somebody still able to clear a level that needs any one sign-off.
  const rescuer =
    level.approverLogic === 'any'
      ? level.approvers.find(
          other =>
            other !== approver &&
            !other.backup &&
            other.status === 'pending' &&
            other.blockedReason === null,
        )
      : undefined;
  const removable =
    approver.backup ||
    level.approvers.some(other => other !== approver && !other.backup);

  const replace = () => {
    if (!choice) {
      setShowRequired(true);
      return;
    }
    onConfirm(choice);
  };

  return (
    <Dialog
      aria-labelledby={TITLE_ID}
      classes={{ paper: styles.dialogPaper }}
      fullScreen={fullScreen}
      onClose={onCancel}
      open
    >
      <DialogTitle className={styles.dialogTitle} disableTypography>
        <Typography id={TITLE_ID} variant="subtitle2">
          <FormattedMessage {...messages.replaceApproverTitle} />
        </Typography>
        <IconButton
          aria-label={intl.formatMessage(globalMessages.close)}
          className={styles.dialogClose}
          onClick={onCancel}
          size="small"
        >
          <Icon icon={XOutlineIcon} size="md" color="inherit" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent} dividers>
        <Typography className={styles.dialogSentence} variant="body2">
          <FormattedMessage
            {...messages.replaceApproverLead}
            values={{ name: approver.name ?? '' }}
          />
          {rescuer?.name && (
            <>
              {' '}
              <FormattedMessage
                {...messages.replaceApproverRescue}
                values={{ name: rescuer.name }}
              />
            </>
          )}{' '}
          <FormattedMessage
            {...(removable
              ? messages.replaceApproverChoose
              : messages.replaceApproverChooseOnly)}
          />
        </Typography>

        {result.isLoading && <CircularProgress size={24} />}

        {result.isError && (
          <div role="alert">
            <Typography className={styles.dialogError} variant="body2">
              <FormattedMessage {...messages.newApproverLoadFailed} />
            </Typography>
            <Button onClick={refetch} size="small">
              <FormattedMessage {...messages.retry} />
            </Button>
          </div>
        )}

        {result.status === 'ready' && candidates.length === 0 && (
          <Typography className={styles.dialogSentence} variant="body2">
            <FormattedMessage
              {...messages.newApproverNone}
              values={{ subject: subjectType }}
            />
          </Typography>
        )}

        {result.status === 'ready' && candidates.length > 0 && (
          // An explicit id, because MUI v4 mints none: without it the label is
          // not the field's accessible name, and the blocker under it is not
          // tied to the field by `aria-describedby`.
          <TextField
            error={showRequired}
            fullWidth
            helperText={
              showRequired
                ? intl.formatMessage(messages.newApproverRequired)
                : undefined
            }
            FormHelperTextProps={{ role: 'alert' }}
            id={FIELD_ID}
            label={intl.formatMessage(messages.newApproverLabel)}
            select
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            value={choice}
            variant="outlined"
            onChange={event => {
              setChoice(event.target.value);
              setShowRequired(false);
            }}
          >
            <MenuItem value="">
              <FormattedMessage {...messages.newApproverPlaceholder} />
            </MenuItem>
            {candidates.map(option => (
              <MenuItem key={option.external_id} value={option.external_id}>
                {option.name ?? option.external_id}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* The server's own sentence, verbatim, as the decision dialogs show
            theirs: it says whether the approver was already moved, or the
            request settled first. */}
        {error && (
          <Typography
            className={styles.dialogError}
            role="alert"
            variant="body2"
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          className={classNames(styles.dialogButton, queueStyles.dialogButtonNoWrap)}
          disabled={submitting}
          variant="outlined"
          onClick={onCancel}
        >
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        {/* Outlined in the error role, never filled: a removal can complete the
            level on fewer sign-offs than it started with. */}
        {removable && (
          <Button
            className={classNames(
              styles.dialogDeleteButton,
              queueStyles.dialogButtonNoWrap,
            )}
            disabled={submitting}
            variant="outlined"
            onClick={() => onConfirm(null)}
          >
            <FormattedMessage {...messages.removeFromRequest} />
          </Button>
        )}
        {/* "Replace", the verb alone, because the title names the approver.
            The accessible name keeps both words, and stays when the label
            turns into a spinner. */}
        <Button
          aria-label={intl.formatMessage(messages.replaceApproverTitle)}
          className={classNames(
            styles.dialogConfirmButton,
            queueStyles.dialogButtonNoWrap,
          )}
          color="primary"
          disabled={submitting || candidates.length === 0}
          variant="contained"
          onClick={replace}
        >
          {submitting ? (
            <CircularProgress color="inherit" size={18} />
          ) : (
            <FormattedMessage {...messages.replaceApproverConfirm} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
